import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/ui/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { axManufacturersApi } from "./api";

export const useGetManuFacturer = (
  page: number,
  search_data?: string,
  country?: string,
  status?: boolean
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.manufacturer.getAllManufacturer(
      remoteUtilityId,
      page,
      search_data,
      country,
      status
    ),
    () =>
      axManufacturersApi.getManuFacturer(
        page,
        remoteUtilityId,
        search_data,
        country,
        status
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetManuFacturerSummary = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.manufacturer.getManufacturerSummary(),
    () => axManufacturersApi.getManuFacturerSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useCreateManufacturer = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axManufacturersApi.createManufacturer(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Manufacturer Created",
          description: "Manufacturer has been created successfully.",
        });
        queryClient.refetchQueries({
          queryKey: ["ax", "manufacturer", "getAllManufacturer"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdateManufacturer = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axManufacturersApi.updateManufacturer(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Manufacturer Updated",
          description: "Manufacturer has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: ["ax", "manufacturer", "getAllManufacturer"],
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.manufacturer.getManufacturerById(
            remoteUtilityId,
            payload.id
          ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdateManufacturerStatus = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axManufacturersApi.updateManufacturerStatus(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "Manufacturer Status Updated",
          description: "Manufacturer status has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.manufacturer.getManufacturerById(
            remoteUtilityId,
            payload.id
          ),
        });
        queryClient.refetchQueries({
          queryKey: ["ax", "manufacturer", "getAllManufacturer"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useGetCountry = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.manufacturer.getCountry(),
    () => axManufacturersApi.getCountry(remoteUtilityId, "country", true),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetAssetType = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.manufacturer.getAssetType(),
    () => axManufacturersApi.getAssetType(remoteUtilityId, "asset_type", true),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetManufacturerById = (id: number) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.manufacturer.getManufacturerById(
      remoteUtilityId,
      id
    ),
    () => axManufacturersApi.getManufacturerById(remoteUtilityId, id),
    {
      enabled: !!remoteUtilityId && !!id,
    }
  );
};

export const useGetAllAssets = (id: number, filters?: any) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.manufacturer.getAllAssets(
      remoteUtilityId,
      id,
      filters
    ),
    () => axManufacturersApi.getAllAssets(remoteUtilityId, id, filters),
    {
      enabled: !!remoteUtilityId && !!id,
    }
  );
};

export const useGetAllAssetStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.manufacturer.getAllAssetStatus(),
    () => axManufacturersApi.getAllAssetStatus(),
    {
      enabled: true,
    }
  );
};
